import { HttpClient, type FetchResult } from '@/libs/http/http-client'
import { get_config } from '@/config'
import type { List, SingleItem } from './types'
import type { DraftUserDTO, UserDTO } from '@bayerntek/identity-lib'
import { TokenAuth } from '@/libs/utils/token-auth'

const config = get_config()
export class IdentityService {
  // Singleton instance
  static instance: IdentityService
  static getInstance(): IdentityService {
    if (!this.instance) {
      this.instance = new IdentityService()
    }
    return this.instance
  }

  private client: HttpClient
  constructor(client?: HttpClient) {
    let baseUrl = config.identity.api
    if (!baseUrl.startsWith('http')) {
      baseUrl = `${location.origin}/${baseUrl}`
    }
    this.client =
      client ||
      new HttpClient({
        serviceName: 'ID-Service',
        baseUrl,
        auth: new TokenAuth(),
      })
  }

  async verify(token: string): Promise<JwtUser> {
    const result = this.client.post({
      path: '/jwt/verify',
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    const response = await result.response()
    return response.json()
  }
  async login(email: string, password: string): Promise<LoginResult> {
    const result = this.client.post({
      path: '/basic/login',
      body: {
        email,
        password,
      },
    })
    const response = await result.response()
    return response.json()
  }

  async authenticate(): Promise<AuthToken> {
    const result = this.client.get({
      path: '/authenticate',
    })
    const response = await result.response()
    return response.json()
  }

  // User
  async post_user(body: DraftUserDTO): Promise<SingleItem<UserDTO>> {
    const result = this.client.post({
      path: `/user`,
      body,
    })
    const response = await result.response()
    return response.json()
  }

  async get_users(
    offset: number,
    limit: number,
    filter: Record<string, string> = {},
  ): Promise<List<UserDTO>> {
    const search_filter =
      Object.keys(filter).length > 0 ? new URLSearchParams(filter) : ''
    const result = this.client.get({
      path: `/user/list?offset=${offset}&limit=${limit}${search_filter ? `&${search_filter.toString()}` : ''}`,
    })
    const response = await result.response()
    return response.json()
  }

  async check_user(data: {
    email: string
    username?: string
  }): Promise<CheckUserResult> {
    const result = this.client.post({
      path: `/user/check`,
      body: data,
    })
    const response = await result.response()
    return response.json()
  }
  async get_user(id: number): Promise<SingleItem<UserDTO>> {
    const result = this.client.get({
      path: `/user/${id}`,
    })
    const response = await result.response()
    return response.json()
  }

  async patch_user(body: UserDTO): Promise<SingleItem<UserDTO>> {
    const result = this.client.patch({
      path: `/user/${body.id}`,
      body,
    })
    const response = await result.response()
    return response.json()
  }

  async delete_user(id: number): Promise<SingleItem<UserDTO>> {
    const result = this.client.delete({
      path: `/user/${id}`,
    })
    const response = await result.response()
    return response.json()
  }

  // Two factor
  async two_factor_setup(user_id: number) {
    const result = this.client.post({
      path: `/two-factor/setup`,
      body: {
        user_id,
      },
    })
    const response = await result.response()
    return response.json()
  }
  async two_factor_verify(user_id: number, token: string) {
    const result = this.client.post({
      path: `/two-factor/verify`,
      body: {
        user_id,
        token,
      },
    })
    const response = await result.response()
    return response.json()
  }
}

export type CountResult<T> = {
  value: T
  count: number
}

export type CheckUserResult = {
  email: CountResult<string>
  username: CountResult<string>
}

export type AuthToken = {
  token: string
}

export type LoginResult = {
  is_signed: boolean
  access_token: string
  user: User
  message: string
}

export type User = {
  id: number
  username: string
  full_name: string
  nick_name: string
  email: string
  phone: string
  roles: string[]
  is_two_factor_enabled: boolean
  is_passkey_enabled: boolean
  created_at: Date
  modified_at: Date
}

export type JwtUser = User & {
  iss?: string
  sub?: string
  aud?: string[] | string
  exp?: number
  nbf?: number
  iat?: number
  jti?: string
}
